import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Timer, Boxes, Megaphone, Store } from "lucide-react";
import { IMAGES } from "../data";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const badges = [
  { icon: Boxes, text: "Pilot from 5,000 cups", color: "bg-[#FBBF24] text-[#2E1065]", tilt: "-3deg", pos: "top-6 -left-2 lg:-left-10", delay: "0s" },
  { icon: Timer, text: "15-Day Delivery", color: "bg-[#0D9488] text-white", tilt: "2deg", pos: "bottom-24 -right-2 lg:-right-8", delay: "1.2s" },
  { icon: Sparkles, text: "3D Digital Proofing", color: "bg-[#2E1065] text-white", tilt: "-2deg", pos: "bottom-2 left-6", delay: "2s" },
];

export const Hero = ({ onQuote, onNav, onAdvertise, onVendor }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgR = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 grain"
      data-testid="hero-section"
    >
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-[#FF5A36]/15 blur-3xl" />
      <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#0D9488]/15 blur-3xl" />
      <div className="absolute top-1/3 left-1/3 w-72 h-72 rounded-full bg-[#FBBF24]/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-8 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
            className="inline-flex items-center gap-2 bg-[#2E1065] text-[#FBBF24] text-xs font-bold uppercase tracking-[0.2em] rounded-full px-4 py-2 mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="w-2 h-2 rounded-full bg-[#FF5A36] animate-pulse" />
            Cup-Based Advertising · Pilot Campaigns Open
          </motion.div>

          <h1 className="font-display font-black tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem]" data-testid="hero-headline">
            <MaskedLine delay={0.15} className="text-[#2E1065]">YOUR BRAND.</MaskedLine>
            <MaskedLine delay={0.3} className="text-[#FF5A36] italic">IN EVERY</MaskedLine>
            <MaskedLine delay={0.45} className="text-[#2E1065]">
              HAND<span className="text-[#0D9488]">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 text-base sm:text-lg text-[#2E1065]/70 leading-relaxed max-w-lg"
            data-testid="hero-subtext"
          >
            Cup Connect is a new kind of ad network: we manufacture branded
            paper cups in our own facility and place them with chai tapris,
            tea stalls, cafés and major IT parks across Karnataka. Your brand
            gets held, not scrolled — 35 minutes at a time. We're now
            onboarding our first cohort of pilot campaigns.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onAdvertise}
              className="group flex items-center gap-2 font-bold text-white bg-[#FF5A36] hover:bg-[#2E1065] rounded-full px-7 py-4 transition-all duration-300 shadow-xl shadow-[#FF5A36]/30 hover:shadow-[#2E1065]/30 hover:-translate-y-1"
              data-testid="hero-advertise-button"
            >
              <Megaphone className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-12" />
              Advertise With Us
            </button>
            <button
              onClick={onVendor}
              className="group flex items-center gap-2 font-bold text-[#2E1065] border-2 border-[#0D9488]/40 hover:border-[#0D9488] hover:bg-[#0D9488]/5 rounded-full px-7 py-4 transition-all duration-300 hover:-translate-y-1"
              data-testid="hero-vendor-button"
            >
              <Store className="w-5 h-5 text-[#0D9488] transition-transform duration-300 group-hover:scale-110" />
              Become a Vendor Partner
            </button>
          </motion.div>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            onClick={() => onQuote()}
            className="mt-5 text-sm font-bold text-[#2E1065]/60 hover:text-[#FF5A36] underline underline-offset-4 decoration-[#2E1065]/20 hover:decoration-[#FF5A36] transition-colors duration-300"
            data-testid="hero-request-quote-link"
          >
            Or request a custom print quote directly →
          </motion.button>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
            className="relative"
          >
            <motion.div
              style={{ y: imgY, rotate: imgR }}
              className="relative rounded-[2.5rem] overflow-hidden border-8 border-white shadow-2xl shadow-[#2E1065]/25"
            >
              <img
                src={IMAGES.hero}
                alt="Stack of custom branded paper cups"
                className="w-full h-[26rem] sm:h-[30rem] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E1065]/40 via-transparent to-transparent" />
            </motion.div>

            {badges.map((b, i) => (
              <motion.div
                key={b.text}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1 + i * 0.15, ease: EASE }}
                className={`absolute ${b.pos} animate-float`}
                style={{ "--tilt": b.tilt, animationDelay: b.delay }}
              >
                <div className={`flex items-center gap-2 ${b.color} font-bold text-xs sm:text-sm rounded-full px-4 py-2.5 shadow-xl`} data-testid={`hero-badge-${i}`}>
                  <b.icon className="w-4 h-4" /> {b.text}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
